/**
 * useCanvasZoom Hook (Story 5.38, Task 1)
 *
 * Manages zoom state for the report editor canvas.
 * Provides zoom in/out, fit-to-width, and clamping to 50%-500% range.
 */

import { useState, useCallback } from "react";

export const ZOOM_MIN = 50;
export const ZOOM_MAX = 500;
export const ZOOM_STEP = 10;

export interface UseCanvasZoomOptions {
  initialZoomLevel?: number;
}

export interface UseCanvasZoomReturn {
  /** Current zoom level (may be a float during pinch gestures) */
  zoomLevel: number;
  /** Rounded zoom for display (e.g. "127%") */
  displayZoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  setZoom: (level: number) => void;
  fitToWidth: (availableWidth: number, contentWidth: number) => void;
}

function clampZoom(level: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, level));
}

export function useCanvasZoom(options?: UseCanvasZoomOptions): UseCanvasZoomReturn {
  const [zoomLevel, setZoomLevel] = useState<number>(
    () => Math.round(clampZoom(options?.initialZoomLevel ?? 100))
  );

  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.round(clampZoom(prev + ZOOM_STEP)));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.round(clampZoom(prev - ZOOM_STEP)));
  }, []);

  const setZoom = useCallback((level: number) => {
    setZoomLevel(clampZoom(level));
  }, []);

  const fitToWidth = useCallback((availableWidth: number, contentWidth: number) => {
    if (contentWidth <= 0) return;
    const calculated = (availableWidth / contentWidth) * 100;
    setZoomLevel(Math.round(clampZoom(calculated)));
  }, []);

  return { zoomLevel, displayZoom: Math.round(zoomLevel), zoomIn, zoomOut, setZoom, fitToWidth };
}
